import type { PriceId, ProductKind } from '@iheartjane/types';

/**
 * Per-price-id field families keyed by the 8 PriceId suffixes
 * (each, eighth_ounce, gram, half_gram, half_ounce, ounce, quarter_ounce,
 * two_gram). v1 PendingCartProduct ships price/discount fields as strings; v3
 * CartProduct ships them as `number | null`. Both satisfy `string | number |
 * null`. The max_cart_quantity family is numeric on both shapes.
 */
type PerPriceId<Prefix extends string, V> = {
  [K in PriceId as `${Prefix}_${K}`]?: V;
};

export interface CartRowProduct
  extends
    PerPriceId<'price', string | number | null>,
    PerPriceId<'discounted_price', string | number | null>,
    PerPriceId<'max_cart_quantity', number | null> {
  aggregate_rating?: number | null;
  amount?: string | null;
  brand?: string | null;
  brand_discount_label?: string | null;
  brand_discount_title?: string | null;
  brand_special_has_segment_rules?: boolean | null;
  category?: string | null;
  count: number;
  kind: ProductKind;
  max_cart_quantity: number | null;
  name: string;
  price_id: PriceId;
  product_id: number;
  review_count?: number | null;
  special_id?: number | null;
  special_title?: string | null;
  // satisfies getCartItemWeight's { price_id, type } param without a cast
  type?: string | null;
}

/**
 * TODO carts-v3 rule 3: retire `getMaxCartQuantity` (needs io-ts MenuProduct) by
 * reading the precomputed cap off the product. Per-weight cap if truthy, else
 * the general cap, else 0 (no limit configured → caller renders disabled).
 */
export const maxQuantityForProduct = (product: CartRowProduct): number =>
  product[`max_cart_quantity_${product.price_id}`] ||
  product.max_cart_quantity ||
  0;
